import React, { useState, useEffect } from 'react';
import { GitMerge, Users, Star, Award, Building2, User } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const AgencyStructure = ({ hideHeader = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHierarchy();
  }, []);

  const fetchHierarchy = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/agencies/hierarchy`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Group data by Manager
  const managers = {};
  data.forEach(item => {
    const managerName = item.manager_name || "Unassigned Manager / Super Admin Direct";
    const managerId = item.manager_id || "direct";
    if (!managers[managerId]) {
      managers[managerId] = {
        name: managerName,
        bds: {}
      };
    }

    const bdName = item.bd_name || "Unassigned BD / Direct Recruitment";
    const bdId = item.bd_id || "direct_bd";
    if (!managers[managerId].bds[bdId]) {
      managers[managerId].bds[bdId] = {
        name: bdName,
        agencies: []
      };
    }

    managers[managerId].bds[bdId].agencies.push({
      id: item.agency_id,
      name: item.agency_name
    });
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {!hideHeader && (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-800">Agency Structure Tree</h2>
            <p className="text-slate-500 mt-1">Interactive network diagram mapping Manager branches, Business Development managers (BDs), and recruited Agencies.</p>
          </div>
          <button 
            onClick={fetchHierarchy}
            className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
          >
            Re-align Tree
          </button>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-3xl border border-slate-100 p-20 text-center shadow-xl">
          <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Mapping structure tree hierarchy...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 p-20 text-center text-slate-400 shadow-xl">
          <GitMerge size={48} className="mx-auto mb-4 opacity-35" />
          <p>No active network hierarchy structures found.</p>
        </div>
      ) : (
        <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 space-y-8 overflow-x-auto">
          {/* Main Network Tree Root */}
          <div className="flex justify-center mb-10">
            <div className="px-6 py-3.5 bg-slate-900 text-white rounded-2xl font-black text-center shadow-lg border border-slate-800">
              <span className="text-[10px] uppercase font-bold text-pink-400 block tracking-widest mb-0.5">XOXO Network Root</span>
              <span className="flex items-center justify-center gap-1.5"><Star size={16} /> Platform Admin Panel</span>
            </div>
          </div>

          <div className="space-y-12 pl-4 border-l-2 border-slate-100">
            {Object.keys(managers).map(mId => {
              const manager = managers[mId];
              return (
                <div key={mId} className="space-y-6 relative">
                  {/* Connector node indicator */}
                  <div className="absolute -left-[22px] top-4 w-3.5 h-3.5 rounded-full bg-slate-900 border-2 border-white"></div>
                  
                  {/* Manager Node */}
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl"><User size={20} /></div>
                    <div>
                      <h4 className="font-extrabold text-slate-800 text-base">{manager.name}</h4>
                      <p className="text-xs text-slate-400">Regional Manager / Branch Leader</p>
                    </div>
                  </div>

                  {/* BD Children */}
                  <div className="pl-12 space-y-8 border-l border-indigo-100">
                    {Object.keys(manager.bds).map(bId => {
                      const bd = manager.bds[bId];
                      return (
                        <div key={bId} className="space-y-4 relative">
                          <div className="absolute -left-[54px] top-4 w-6 h-[2px] bg-indigo-100"></div>
                          
                          {/* BD Node */}
                          <div className="flex items-center gap-2">
                            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg"><GitMerge size={16} /></div>
                            <div>
                              <h5 className="font-bold text-slate-700 text-sm">{bd.name}</h5>
                              <p className="text-[10px] text-slate-400">Business Development Leader</p>
                            </div>
                          </div>

                          {/* Recruited Agencies Grid */}
                          <div className="pl-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {bd.agencies.map(agency => (
                              <div key={agency.id} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:border-pink-300 hover:bg-pink-50/10 hover:shadow-md transition duration-200">
                                <div className="flex items-center gap-2">
                                  <Building2 size={16} className="text-pink-600" />
                                  <h6 className="font-bold text-slate-800 text-xs">{agency.name}</h6>
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1 uppercase font-semibold tracking-wider">Recruitment ID: #{agency.id.substring(0, 8)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyStructure;
